import { FC, useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonGrid,
  IonRow,
  IonCol,
  IonText,
  IonIcon,
  IonButtons,
  IonButton,
  IonBackButton,
  IonSpinner,
} from "@ionic/react";
import { ICart, PaymentMethod } from "../../models";
import { createCardOrderApi } from "../Payment/paymentApi";
import { usePaymentConfig } from "../Payment/usePaymentConfig";
import {
  getRecaptchaToken,
  setPendingToken,
  submitGatewayForm,
} from "../Payment/gateway";
import { arrowBackCircle, arrowForwardCircle } from "ionicons/icons";
import "../../theme/common.css";
import { debug, getLocalBool, getLocalObject } from "../../common/utils";
import { NumberMoney } from "../../components/NumberMoney/NumberMoney";
import { useCart } from "./useCart";
import { CartOrder } from "./CartOrder";
import { CartInfo } from "./CartInfo";
import PicturePreview from "../Pictures/PicturePreview";
import InfoMessage from "../../components/InfoMessage/InfoMessage";
import appconfig from "../../AppConfig";
import { OrderAccepted } from "./OrderAccepted";
import { usePictures } from "../Pictures/usePictures";
import { OrderConfirmation } from "./OrderConfirmation";
import {
  BIH_PRICE_MESSAGE,
  BIH_MAX_ORDER_PRICE_KM,
  BIH_WARNING_ORDER_PRICE_KM,
  BIH_PRICE_LIMIT_MESSAGE,
} from "../../common/constants";
import { convertPrice } from "../../common/currencyUtils";
import { useCommon } from "../../common/useCommon";
import { DeliveryText } from "../PackageContent/Content";
import { PaymentMethod as PaymentMethodPicker } from "./PaymentMethod";
import { firstInvalidField, focusOrderField, validateOrder } from "./orderValidation";

const emptyCart = {
  id: -1,
  name: "",
  address: "",
  zip: "",
  place: "",
  phone: "",
  email: "",
  note: "",
  cartItems: [],
};

const Cart: FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const { currency, conversionRate } = useCommon();
  const { allPictures, getAllPictures } = usePictures();

  const {
    loadCart,
    loadReserve,
    loadEaselWood,
    loadEaselAlum,
    loadFrames,
    loadFramesAssembled,
    loadFramesAsPictures,
    loadFramesAssembledAsPictures,
    loadColorPack,
    loadBrandedBag,
    page,
    setPage,
    cart,
    cartPictures,
    cartItems,
    isCartEmpty,
    framesAsPictures,
    framesAssembledAsPictures,
    setFrameAsCart,
    setFrameAssembledAsCart,
    priceTotal,
    orderSubmit,
    resetCart,
  } = useCart();

  const { config: paymentConfig } = usePaymentConfig();
  // No preselected method while card payment is offered (bank requirement); the customer picks one.
  // While card payment is off the picker is hidden and every order is cash on delivery.
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const method: PaymentMethod | null =
    paymentConfig.mode === "off" ? "COD" : paymentMethod;
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  // Reservations are saved with the order but not charged, by card or on delivery.
  // A cart of reservations only has nothing to pay by card.
  const onlyReservations =
    cartItems.length > 0 && cartItems.every((i) => i.isReservation);
  const cardBlockedReason = onlyReservations
    ? "Korpa sadrži samo rezervacije, koje se ne naplaćuju unapred."
    : null;
  const isCard = method === "CARD";

  useEffect(() => {
    if (isCard && (cardBlockedReason || paymentConfig.mode !== "on")) {
      setPaymentMethod(null);
    }
  }, [isCard, cardBlockedReason, paymentConfig.mode]);

  const [showOrderAccepted, setShowOrderAccepted] = useState<boolean>(false);
  const [orderId, setOrderId] = useState<number>(0);
  // The order button is always enabled; a click with something missing shows every error.
  const [showAllErrors, setShowAllErrors] = useState<boolean>(false);
  const [orderButtonLoading, setOrderButtonLoading] = useState<boolean>(false);
  const [newCart, setNewCart] = useState<ICart>(emptyCart as ICart);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

  const priceTotalKM =
    currency === "KM" ? convertPrice(priceTotal(), "KM", conversionRate) : 0;
  const isPriceOverLimit =
    currency === "KM" && priceTotalKM > BIH_MAX_ORDER_PRICE_KM;
  const isPriceWarning =
    currency === "KM" &&
    priceTotalKM >= BIH_WARNING_ORDER_PRICE_KM &&
    priceTotalKM <= BIH_MAX_ORDER_PRICE_KM;
  const formattedPriceKM = priceTotalKM.toFixed(2).replace(".", ",") + " KM";

  useEffect(() => {
    debug("LOAD DEFAULTS");

    setPage(1);

    if (allPictures.length === 0) {
      getAllPictures();
    }

    loadCart(getLocalObject("bpb.cart") || []);
    loadReserve(getLocalObject("bpb.reserve") || []);
    loadEaselWood(getLocalObject("bpb.easelWood") || 0);
    loadEaselAlum(getLocalObject("bpb.easelAlum") || 0);
    loadFrames(getLocalObject("bpb.frames") || 0);
    loadFramesAssembled(getLocalObject("bpb.framesAssembled") || 0);
    loadFramesAsPictures(getLocalBool("bpb.framesAsPictures") || false);
    loadFramesAssembledAsPictures(
      getLocalBool("bpb.framesAssembledAsPictures") || false,
    );
    loadColorPack(getLocalObject("bpb.colorPack") || 0);
    loadBrandedBag(getLocalObject("bpb.brandedBag") || 0);

    return setShowOrderAccepted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // cartPictures.length - do not fire setFramesAsPictures if cart is empty (no pictures) - order only frame(s)
    if (framesAsPictures && cartPictures.length) {
      setFrameAsCart();
    }
  }, [cart, cartPictures, framesAsPictures, setFrameAsCart]);

  useEffect(() => {
    // cartPictures.length - do not fire setFramesAsPictures if cart is empty (no pictures) - order only frame(s) assembled
    if (framesAssembledAsPictures && cartPictures.length) {
      setFrameAssembledAsCart();
    }
  }, [cart, cartPictures, framesAssembledAsPictures, setFrameAssembledAsCart]);

  const orderErrors = validateOrder(newCart, {
    method,
    paymentSection: paymentConfig.mode !== "off",
    termsAccepted,
  });

  // NARUČI: open the confirmation, or mark what is missing and take the customer to the first one.
  const onOrderClick = () => {
    const first = firstInvalidField(orderErrors);
    if (first) {
      setShowAllErrors(true);
      focusOrderField(first);
    } else {
      setIsConfirmationOpen(true);
    }
  };

  const dismissConfirmation = () => {
    setPage(1);
    setShowOrderAccepted(false);
  };

  const updateCart = (cart: ICart) => {
    setNewCart(cart);
  };

  const orderSuccessfull = (id: number) => {
    if (id > 0) {
      setOrderId(id);
      setShowOrderAccepted(true);
      resetCart();
      setPaymentMethod(null);
      setShowAllErrors(false);
      setIsConfirmationOpen(false);
      setOrderButtonLoading(false);
    }
  };

  const order = () => {
    if (newCart && cartItems.length > 0) {
      setOrderButtonLoading(true);
      setOrderError(null);
      const cartWithItems = { ...newCart, cartItems: [...cartItems] } as ICart;
      if (isCard) {
        orderWithCard(cartWithItems);
      } else {
        orderSubmit(cartWithItems, (id) => orderSuccessfull(id));
      }
    }
  };

  // Card: the server prices and saves the order, then the browser goes to the bank's page.
  // The local cart is kept until the payment succeeds, so a failed payment can be retried.
  const orderWithCard = async (cartWithItems: ICart) => {
    const recaptchaToken = await getRecaptchaToken(paymentConfig.recaptchaSiteKey);
    const result = await createCardOrderApi(
      cartWithItems,
      priceTotal(),
      termsAccepted,
      recaptchaToken
    );
    if (result.ok) {
      setPendingToken(result.data.token);
      submitGatewayForm(result.data.form);
    } else {
      setOrderError(result.error);
      setOrderButtonLoading(false);
    }
  };

  return (
    <IonPage id="cart">
      <IonHeader>
        <IonToolbar color="success">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/"></IonBackButton>
          </IonButtons>
          <IonTitle>Korpa za naručivanje</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonToolbar
        className="ion-no-padding"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          boxShadow: scrolled ? "0 2px 8px rgba(0,0,0,0.25)" : "none",
          transition: "box-shadow 0.3s ease",
        }}
      >
        <IonGrid className="ion-no-padding">
          <IonRow className="vertical-center">
            <IonCol size="4">
              {page === 2 && (
                <IonButton color="primary" onClick={() => setPage(1)}>
                  <IonIcon slot="start" icon={arrowBackCircle} />
                  Nazad
                </IonButton>
              )}
            </IonCol>
            <IonCol size="4" className="ion-text-center">
              <IonText className="f14">
                <span style={{ fontSize: 12 }}>UKUPNO </span>
                <br />
                <NumberMoney value={priceTotal()} />
              </IonText>
            </IonCol>
            <IonCol size="4" className="ion-text-right">
              {page === 1 && (
                <IonButton
                  color="primary"
                  disabled={isCartEmpty}
                  onClick={() => setPage(2)}
                >
                  Napred
                  <IonIcon slot="end" icon={arrowForwardCircle} />
                </IonButton>
              )}
              {page === 2 && (
                <IonButton
                  color="primary"
                  disabled={
                    cartItems.length === 0 ||
                    orderButtonLoading ||
                    isPriceOverLimit
                  }
                  expand="block"
                  onClick={onOrderClick}
                >
                  {orderButtonLoading ? (
                    <IonSpinner name="crescent" />
                  ) : (
                    <>NARUČI...</>
                  )}
                </IonButton>
              )}
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonToolbar>

      <IonContent
        scrollEvents
        onIonScroll={(e) => setScrolled(e.detail.scrollTop > 0)}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 5,
            padding: 5,
          }}
        >
          {appconfig.showDeliveryInfoBanner && <DeliveryText centered />}
          {currency === "KM" && (
            <InfoMessage text={BIH_PRICE_MESSAGE} level="warning" />
          )}
          {currency === "KM" && (isPriceWarning || isPriceOverLimit) && (
            <InfoMessage
              text={BIH_PRICE_LIMIT_MESSAGE(formattedPriceKM)}
              level={isPriceOverLimit ? "danger" : "warning"}
            />
          )}
        </div>
        <PicturePreview />

        {page === 1 && <CartInfo />}
        {page === 2 && (
          <CartOrder
            cart={newCart}
            updateCart={updateCart}
            emailRequired={isCard}
            errors={orderErrors}
            showAllErrors={showAllErrors}
            paymentSection={
              <PaymentMethodPicker
                config={paymentConfig}
                method={method}
                onMethodChange={setPaymentMethod}
                cardBlockedReason={cardBlockedReason}
                termsAccepted={termsAccepted}
                onTermsChange={setTermsAccepted}
                totalDin={priceTotal()}
                hasReservations={cartItems.some((i) => i.isReservation)}
                methodError={showAllErrors ? orderErrors.paymentMethod : undefined}
                termsError={showAllErrors ? orderErrors.terms : undefined}
              />
            }
          />
        )}
      </IonContent>
      {isConfirmationOpen && (
        <OrderConfirmation
          isOpen={isConfirmationOpen}
          cart={newCart}
          onCancel={() => {
            setIsConfirmationOpen(false);
            setOrderError(null);
          }}
          onSubmit={order}
          isSubmitting={orderButtonLoading}
          paymentMethod={method ?? undefined}
          error={orderError}
          totalDin={priceTotal()}
        />
      )}
      {showOrderAccepted && (
        <OrderAccepted
          show={showOrderAccepted}
          dismiss={dismissConfirmation}
          orderId={orderId || 0}
        />
      )}
    </IonPage>
  );
};

export default Cart;
